import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output, ViewChild} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_CRUD_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {analyseCondition, describeCondition, formatConditionDate, ConditionAnalysis, ConditionNode, questionnaireOffersVariable,
  simulateWorkflow, StepTestResult, TestAssignment, TestValue, clientVariableDependencies} from './workflow-condition';

interface TestField { key: string; source: string; names: string[]; target: any; }
interface TestGroup { title: string; fields: TestField[]; unavailable: boolean; }
interface DiagramStage { groups: TestGroup[]; conditional: boolean; condition: ConditionAnalysis; }
interface FlowEdge { id: string; path: string; index: number; branch: 'incoming' | 'yes' | 'no' | 'exit'; }
interface Point { x: number; y: number; }
export interface WorkflowTestComponent extends Translatable {}

/** Simulates mapped submissions and visualizes their effect on the workflow route. */
@Component({
  selector: 'app-workflow-test',
  templateUrl: './workflow-test.component.html',
  styleUrls: ['./workflow-test.component.css']
})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class WorkflowTestComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() workflow: any;
  @Input() questionnaires: any[] = [];
  @Input() active = false;
  @Output() editStage = new EventEmitter<number>();
  @ViewChild('diagram') diagram: ElementRef<HTMLElement>;
  @ViewChild('marker') marker: ElementRef<SVGCircleElement>;
  public stages: DiagramStage[] = [];
  public inspected: number = null;
  private inspectorTrigger: HTMLElement;
  public inputs = new Map<string, TestValue>();
  public enabled = new Set<string>();
  public results: StepTestResult[] = null;
  public previewResults: StepTestResult[] = [];
  public completed = new Set<number>();
  public evaluated = new Set<number>();
  public visitedEdges = new Set<string>();
  public edges: FlowEdge[] = [];
  public current = -1;
  public busy = false;
  public playing = false;
  public paused = false;
  public stale = false;
  public finished = false;
  public diagramHeight = 0;
  public diagramWidth = 0;
  public readonly KEYS = KEYS;
  private signature = '';
  private lastQuestionnaires: any[];
  private observer: ResizeObserver;
  private frame = 0;
  private layoutFrame = 0;
  private resolveAnimation: (completed: boolean) => void;
  private generation = 0;
  private destroyed = false;

  /** Keeps animation frames outside Angular's form change detection. */
  constructor(private zone: NgZone, private translationService: TranslationService) {}

  /** Rebuilds per-questionnaire inputs only when their workflow or metadata changes. */
  public ngOnChanges(): void {
    const signature = JSON.stringify(this.workflow);
    if (signature !== this.signature || this.lastQuestionnaires !== this.questionnaires) {
      const hadResults = !!this.results;
      this.reset();
      this.stale = hadResults;
      this.signature = signature;
      this.lastQuestionnaires = this.questionnaires;
      this.inspected = null;
      this.inputs.clear();
      this.enabled.clear();
      this.stages = (this.workflow?.stages || []).map((stage: any, index: number) => {
        const groups: TestGroup[] = [];
        const variables: any[] = this.workflow.variables || [];
        (stage.questionnaires || []).forEach((id: string, position: number) => {
          const questionnaire = this.questionnaires.find(q => (q.id || q._id) === id);
          const fields = new Map<string, TestField>();
          for (const variable of variables) {
            if (variable.target?.type !== 'questionnaire' ||
                !questionnaireOffersVariable(questionnaire, variable.target.variable)) { continue; }
            this.addField(fields, variable, index + ':' + position);
          }
          groups.push({title: questionnaire?.meta?.title?.de_DE || id, fields: Array.from(fields.values()), unavailable: !questionnaire});
        });
        const surveyFields = new Map<string, TestField>();
        variables.filter(v => v.target?.type === 'survey').forEach(v => this.addField(surveyFields, v, index + ':survey'));
        for (const source of Array.from(surveyFields.keys())) {
          for (const dependency of clientVariableDependencies(source)) {
            if (surveyFields.has(dependency)) { continue; }
            surveyFields.set(dependency, {key: index + ':survey:' + dependency, source: dependency, names: [],
              target: {type: 'survey', variable: dependency, options: {format: dependency === 'client.birthDate' ? 'date' : 'text'}}});
          }
        }
        if (surveyFields.size) {
          groups.push({title: this._(KEYS.CRUD.TEST_SURVEY_VALUES), fields: Array.from(surveyFields.values()), unavailable: false});
        }
        const analysis = analyseCondition(stage.expression);
        return {groups, conditional: index > 0 && (!!analysis.root || analysis.error),
          condition: analysis};
      });
      this.previewResults = this.evaluatePlan();
    }
    if (!this.active && this.busy) { this.pause(); }
    this.scheduleLayout();
  }

  /** Groups aliases of the same submission variable into a single input. */
  private addField(fields: Map<string, TestField>, variable: any, prefix: string): void {
    const source = variable.target.variable;
    if (!fields.has(source)) { fields.set(source, {key: prefix + ':' + source, source, names: [], target: variable.target}); }
    fields.get(source).names.push(variable.name);
  }

  /** Repositions connectors when cards wrap, expand or become visible. */
  public ngAfterViewInit(): void {
    this.observer = new ResizeObserver(() => this.scheduleLayout());
    this.observer.observe(this.diagram.nativeElement);
    this.scheduleLayout();
  }

  /** Releases observers and any in-flight animation when navigating away. */
  public ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
    cancelAnimationFrame(this.layoutFrame);
    this.cancelAnimation();
  }

  /** Schedules a single connector measurement after the next browser layout. */
  public scheduleLayout(): void {
    if (this.destroyed || !this.diagram || this.layoutFrame) { return; }
    this.zone.runOutsideAngular(() => {
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = 0;
        if (this.active) { this.zone.run(() => this.layout()); }
      });
    });
  }

  /** Routes execution down the center and skipped steps around the right side. */
  private layout(): void {
    const host = this.diagram.nativeElement;
    const bounds = host.getBoundingClientRect();
    if (!bounds.width) { return; }
    const rect = (selector: string) => host.querySelector(selector)?.getBoundingClientRect();
    const start = rect('.flow-start');
    if (!start) { return; }
    const center = start.left + start.width / 2 - bounds.left;
    let previous: Point = {x: center, y: start.bottom - bounds.top};
    const edges: FlowEdge[] = [];
    this.stages.forEach((stage, index) => {
      const card = rect('[data-card="' + index + '"]');
      const row = rect('[data-row="' + index + '"]');
      const decision = rect('[data-decision="' + index + '"]');
      if (!card || !row) { return; }
      const top = card.top - bounds.top;
      const bottom = card.bottom - bounds.top;
      const join = row.bottom - bounds.top;
      const add = (branch: FlowEdge['branch'], path: string) => edges.push({id: branch + '-' + index, branch, path, index});
      if (decision) {
        const y = decision.top + decision.height / 2 - bounds.top;
        add('incoming', `M ${center} ${previous.y} L ${center} ${decision.top - bounds.top}`);
        add('yes', `M ${center} ${decision.bottom - bounds.top} L ${center} ${top}`);
        const right = bounds.width - 24;
        add('no', `M ${decision.right - bounds.left} ${y} H ${right - 12} Q ${right} ${y} ${right} ${y + 12} V ${join - 12} Q ${right} ${join} ${right - 12} ${join} H ${center}`);
      } else { add('incoming', `M ${center} ${previous.y} L ${center} ${top}`); }
      add('exit', `M ${center} ${bottom} L ${center} ${join}`);
      previous = {x: center, y: join};
    });
    const end = rect('.flow-end');
    if (end) { edges.push({id: 'end', index: this.stages.length, branch: 'exit', path: `M ${center} ${previous.y} L ${center} ${end.top - bounds.top}`}); }
    this.edges = edges;
    this.diagramWidth = bounds.width;
    this.diagramHeight = bounds.height;
  }

  /** Selects a primitive input compatible with the configured variable format. */
  public inputType(field: TestField): string {
    if (field.target?.type === 'survey' && field.source === 'client.gender') { return 'gender'; }
    const opts = field.target?.options || {};
    if (opts.dataType === 'boolean' || opts.format === 'boolean') { return 'boolean'; }
    if (opts.dataType === 'number' || opts.format === 'numeric') { return 'number'; }
    if (opts.format === 'date') {
      if (opts.fullDateTime) { return 'datetime-local'; }
      return opts.yearDate || opts.monthDate ? 'text' : 'date';
    }
    return 'text';
  }

  /** Stores a disposable answer and invalidates the previous route. */
  public setInput(key: string, value: TestValue): void {
    this.inputs.set(key, value);
    this.enabled.add(key);
    this.invalidate(key);
  }

  /** Distinguishes preserving a previous value from explicitly setting an empty value. */
  public toggleValue(key: string, enabled: boolean): void {
    if (enabled && !this.enabled.has(key)) {
      const index = Number(key.split(':')[0]);
      const field = this.stages[index].groups.flatMap(group => group.fields).find(item => item.key === key);
      const value = field && this.inputValue(index, field);
      if (value !== undefined) { this.inputs.set(key, value); }
    }
    enabled ? this.enabled.add(key) : this.enabled.delete(key);
    this.invalidate(key);
  }

  /** Cancels playback after an input edit without discarding entered answers. */
  private invalidate(key: string): void {
    const index = Number(key.split(':')[0]);
    this.previewResults = this.evaluatePlan();
    if (this.results && !this.finished && index >= this.current && (!this.busy || this.paused)) {
      this.results = this.previewResults;
      return;
    }
    const hadResults = !!this.results;
    this.reset();
    this.stale = hadResults;
  }

  /** Builds ordered submissions so later questionnaires can overwrite earlier values. */
  private evaluatePlan(): StepTestResult[] {
    const submissions: TestAssignment[][] = this.stages.map(stage => stage.groups.flatMap(group => group.fields.flatMap(field => {
      if (!this.enabled.has(field.key)) { return []; }
      const value = this.inputs.get(field.key);
      const type = this.inputType(field);
      let typed: TestValue;
      if (type === 'text') { typed = value === undefined ? '' : String(value); }
      else if (type === 'gender' && ['male', 'female', 'diverse'].includes(String(value))) { typed = value; }
      else if (type === 'boolean' && typeof value === 'boolean') { typed = value; }
      else if (type === 'number' && value !== '' && value != null && Number.isFinite(Number(value))) { typed = Number(value); }
      else if ((type === 'date' || type === 'datetime-local') && value) { typed = String(value); }
      else { return []; }
      return [{names: field.names, value: typed, source: field.target?.type === 'survey' ? field.source : undefined}];
    })));
    return simulateWorkflow(this.workflow, submissions, text => this._(text));
  }

  /** Displays automatic patient values without turning them into explicit overrides. */
  public inputValue(index: number, field: TestField): TestValue {
    if (this.enabled.has(field.key)) { return this.inputs.get(field.key); }
    return field.target?.type === 'survey' ? this.previewResults[index]?.clientValues[field.source] : this.inputs.get(field.key);
  }

  /** Marks generated values separately from manual or preserved patient data. */
  public isAutomatic(index: number, field: TestField): boolean {
    return field.target?.type === 'survey' && !this.enabled.has(field.key) &&
      this.previewResults[index]?.derivedValues[field.source] !== undefined;
  }

  /** Starts or resumes automatic playback using the current local answers. */
  public run(): void {
    if (this.finished) { this.reset(); }
    if (!this.results) { this.results = this.evaluatePlan(); this.current = 0; this.stale = false; }
    this.inspected = null;
    this.playing = true;
    this.paused = false;
    if (!this.busy) { void this.playStep(); }
  }

  /** Freezes the moving marker while keeping its current progress. */
  public pause(): void { this.playing = false; this.paused = this.busy; }

  /** Executes one step, or resumes the current step and pauses after it. */
  public advance(): void {
    if (this.finished) { return; }
    if (!this.results) { this.results = this.evaluatePlan(); this.current = 0; this.stale = false; }
    this.playing = false;
    this.paused = false;
    if (!this.busy) { void this.playStep(); }
  }

  /** Clears playback and results while preserving the prepared answers. */
  public reset(): void {
    this.cancelAnimation();
    this.results = null;
    this.completed.clear();
    this.evaluated.clear();
    this.visitedEdges.clear();
    this.current = -1;
    this.busy = this.playing = this.paused = this.finished = this.stale = false;
    this.marker?.nativeElement.setAttribute('visibility', 'hidden');
  }

  /** Cancels pending frames and resolves their promises so no stale run can continue. */
  private cancelAnimation(): void {
    this.generation++;
    cancelAnimationFrame(this.frame);
    this.resolveAnimation?.(false);
    this.resolveAnimation = null;
  }

  /** Reveals a decision, follows its branch, then publishes the resulting variable changes. */
  private async playStep(): Promise<void> {
    const generation = this.generation;
    const index = this.current;
    const step = this.results?.[index];
    if (!step) { return; }
    this.busy = true;
    this.diagram.nativeElement.querySelector('[data-row="' + index + '"]')?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    if (!await this.travel('incoming-' + index) || generation !== this.generation) { return; }
    this.evaluated.add(index);
    if (step.result === null) {
      this.busy = this.playing = this.paused = false;
      this.finished = true;
      return;
    }
    if (this.stages[index].conditional && !await this.travel((step.result ? 'yes-' : 'no-') + index)) { return; }
    if (step.result && !await this.travel('exit-' + index, 600)) { return; }
    if (generation !== this.generation) { return; }
    this.completed.add(index);
    this.current++;
    if (this.current === this.stages.length) {
      if (!await this.travel('end') || generation !== this.generation) { return; }
      this.finished = true;
      this.playing = false;
    }
    this.busy = false;
    if (this.playing && !this.finished) { void this.playStep(); }
  }

  /** Animates a marker on the actual SVG connector, honoring pause and reduced motion. */
  private async travel(id: string, hold = 0): Promise<boolean> {
    const generation = this.generation;
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    if (generation !== this.generation || this.destroyed) { return false; }
    this.layout();
    return new Promise(resolve => {
      const path = this.diagram.nativeElement.querySelector<SVGPathElement>('[data-edge="' + id + '"]');
      if (!path) { resolve(false); this.busy = this.playing = false; return; }
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const length = path.getTotalLength();
      const duration = reduced ? 1 : Math.max(350, Math.min(1500, length * 3));
      const wait = reduced ? 0 : hold;
      let elapsed = 0;
      let last = 0;
      this.resolveAnimation = resolve;
      this.zone.runOutsideAngular(() => {
        const tick = (now: number) => {
          if (!this.paused && last) { elapsed += Math.min(now - last, 64); }
          last = now;
          const progress = Math.max(0, Math.min(1, (elapsed - wait) / duration));
          const currentPath = this.diagram.nativeElement.querySelector<SVGPathElement>('[data-edge="' + id + '"]') || path;
          const point = currentPath.getPointAtLength(currentPath.getTotalLength() * progress);
          const marker = this.marker.nativeElement;
          marker.setAttribute('cx', String(point.x));
          marker.setAttribute('cy', String(point.y));
          marker.setAttribute('visibility', 'visible');
          if (progress < 1) { this.frame = requestAnimationFrame(tick); return; }
          this.zone.run(() => {
            this.visitedEdges.add(id);
            this.resolveAnimation = null;
            resolve(true);
          });
        };
        this.frame = requestAnimationFrame(tick);
      });
    });
  }

  /** Opens step inputs separately from the compact diagram and pauses any moving marker. */
  public toggleInputs(index: number, event?: Event, fieldKey?: string): void {
    if (this.busy) { this.pause(); }
    this.inspected = index;
    this.inspectorTrigger = event?.currentTarget as HTMLElement;
    setTimeout(() => {
      if (this.destroyed || this.inspected !== index) { return; }
      const dialog = this.diagram.nativeElement.parentElement.querySelector('.test-inspector');
      const controls = Array.from(dialog?.querySelectorAll<HTMLElement>('input, select, button') || []);
      const input = fieldKey ? controls.find(element => element.id === 'test-' + fieldKey) : controls[0];
      input?.focus();
      if (fieldKey) { input?.scrollIntoView({block: 'nearest'}); }
    });
  }

  /** Closes the input overlay and returns keyboard focus to its trigger. */
  public closeInspector(): void {
    this.inspected = null;
    this.inspectorTrigger?.focus();
  }

  /** Keeps keyboard navigation inside the open modal and supports Escape. */
  @HostListener('document:keydown', ['$event'])
  public onInspectorKey(event: KeyboardEvent): void {
    if (this.inspected === null) { return; }
    if (event.key === 'Escape') { event.preventDefault(); this.closeInspector(); return; }
    if (event.key !== 'Tab') { return; }
    const dialog = this.diagram.nativeElement.parentElement.querySelector<HTMLElement>('.test-inspector');
    const controls = Array.from(dialog?.querySelectorAll<HTMLElement>('button:not([disabled]), input, select, summary, [tabindex="0"]') || []);
    if (!controls.length) { return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /** Identifies a reached decision that cannot continue with the current test data. */
  public isBlocked(index: number): boolean {
    return this.evaluated.has(index) && this.results?.[index]?.result === null;
  }

  /** Explains the first unresolved comparison and finds an executed source step for its input. */
  public blockingDetails(index: number): {variable: string; reason: string; sourceIndex: number; fieldKey: string} {
    const result = this.results?.[index];
    const trace = result?.traces.find(item => item.result === null);
    const variable = trace?.rule.kind === 'rule' ? trace.rule.variable : '';
    const details = {variable, reason: trace?.reason || result?.reason, sourceIndex: -1, fieldKey: ''};
    if (!variable || trace.reason !== KEYS.CRUD.MISSING_EXAMPLE) { return details; }
    for (let sourceIndex = index - 1; sourceIndex >= 0; sourceIndex--) {
      if (!this.completed.has(sourceIndex) || this.results[sourceIndex].result !== true) { continue; }
      const fields = this.stages[sourceIndex].groups.flatMap(group => group.fields);
      const field = fields.slice().reverse().find(item => item.names.includes(variable));
      if (field) { return {...details, sourceIndex, fieldKey: field.key}; }
    }
    details.reason = KEYS.CRUD.LATE_VARIABLE;
    return details;
  }

  /** Opens the preceding submission and focuses the value needed by the blocked decision. */
  public completeMissingInput(index: number, event: Event): void {
    const details = this.blockingDetails(index);
    if (details.sourceIndex < 0) { return; }
    this.toggleInputs(details.sourceIndex, event, details.fieldKey);
  }

  /** Counts configured submission values without expanding the diagram node. */
  public preparedCount(index: number): number {
    return this.stages[index].groups.flatMap(group => group.fields).filter(field => this.enabled.has(field.key)).length;
  }

  /** Returns the current value before a step without exposing future results. */
  public previousValue(index: number, field: TestField): string {
    const previous = index > 0 && this.completed.has(index - 1) ? this.results?.[index - 1]?.after : {};
    const value = previous?.[field.names[0]];
    return value === undefined ? this._(KEYS.CRUD.TEST_UNSET) : this.formatValue(value);
  }

  /** Returns the configured step name or a numbered fallback. */
  public stageName(index: number): string {
    return this.workflow.stages[index]?.name?.de_DE?.trim() || this._(KEYS.CRUD.STAGE) + ' ' + (index + 1);
  }

  /** Describes comparisons in the active locale. */
  public describe(node: ConditionNode): string {
    return describeCondition(node, text => this._(text),
      (name, value) => formatConditionDate(value, this.workflow.variables?.find(variable => variable.name === name),
        token => this.translationService.getDateFormat(token)));
  }

  /** Formats primitive values without hiding zero, false or an empty string. */
  public formatValue(value: TestValue): string {
    return value === undefined ? this._(KEYS.CRUD.TEST_UNSET) :
      typeof value === 'boolean' ? this._(value ? KEYS.CRUD.YES : KEYS.CRUD.NO) : JSON.stringify(value);
  }
}
